import { FC, useEffect, useMemo } from 'react';

import { DropdownOption, MultiSelect, UseFormReturn } from '@dbt-labs/sourdough';

import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useFilterContext } from '@/context/FilterProvider';
import { useSearchFacets } from '@/context/SearchFacetsProvider';
import { useProjects } from '@/hooks/admin/project';
import { FilterSection } from './FilterSection';

export type MultiProjectFilterProps = {
  formMethods: UseFormReturn<any>;
};

export const MultiProjectFilter: FC<MultiProjectFilterProps> = ({ formMethods }) => {
  const { accountId } = useDbtCloudAccount();
  const accountProjects = useProjects({ accountId: accountId! });
  const filterCtx = useFilterContext();
  const { aggregations } = useSearchFacets();

  const projects = useMemo(
    () => filterCtx.filters?.projects || [],
    [filterCtx.filters],
  );

  const options: DropdownOption[] = useMemo(
    () =>
      (accountProjects.data || []).map((project) => {
        // Backend returns project aggregations with project name as key
        const count = aggregations?.projects?.[project.name];
        const label = count !== undefined ? `${project.name} (${count})` : project.name;
        return {
          label,
          value: project.id!,
        };
      }),
    [accountProjects.data, aggregations],
  );

  const selectedOptions: DropdownOption[] = useMemo(
    () =>
      options
        .filter((option) => projects.includes(Number(option.value)))
        .map((option) => ({
          label: option.label,
          value: option.value,
        })),
    [options, projects],
  );

  useEffect(() => {
    if (selectedOptions.length === 0) return;
    formMethods.setValue('projects', selectedOptions);
  }, [formMethods, selectedOptions]);

  return (
    <FilterSection title="Projects">
      <div className="mt-2">
        <MultiSelect
          name="projects"
          placeholder="Find a project"
          isEdit
          skeleton={accountProjects.isFetching}
          options={options}
          defaultValue={selectedOptions}
          showSelectAll={options.length > 1}
          onChange={(selectedOptions) => {
            const values = selectedOptions.map((option) => Number(option.value));
            const itemsToAdd = values.filter((id) => !projects.includes(id));
            const itemsToDelete = projects.filter((id) => !values.includes(id));

            if (itemsToAdd.length) {
              filterCtx.updateFilters({ projects: itemsToAdd }, true);
            }
            if (itemsToDelete.length) {
              filterCtx.updateFilters({ projects: itemsToDelete }, false);
            }
          }}
        />
      </div>
    </FilterSection>
  );
};
